import React, { useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import useDeskSymbol from './useDeskSymbol';
import DecisionCard from '../../home/today/DecisionCard';
import CycleRuler from './CycleRuler';

// The Desk's right-hand panel (DWLF-300; canvas board AppDesk): "Today for {symbol}" — the read,
// the event stream and any decision waiting, next to the chart it is about. Brief / Events / Decide.

const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' };
const MONO = '"IBM Plex Mono", monospace';
const fmt = (v) => (Number.isFinite(v) ? v.toLocaleString('en-GB', { maximumFractionDigits: v >= 100 ? 2 : 4 }) : '—');
// Every date on the Desk is a bar's day ('2026-08-07', an ISO time or epoch ms), and bars are UTC days: format in UTC so
// a viewer behind UTC never sees the day before (the chart labels the same bars in UTC).
const fmtDate = (v) => {
  if (!v) {return '—';}
  const t = new Date(typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00Z` : v);
  return Number.isNaN(t.getTime()) ? '—' : t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, ' ') : '—');
const toneOf = (v) => (/bull|up|rising|markup/i.test(v || '') ? 'up' : /bear|down|falling|markdown/i.test(v || '') ? 'down' : 'text');

function Row({ k, v, tone = 'text', hint }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, minHeight: 36, borderBottom: `1px solid ${tok('ink-06')}` }}>
      <Typography component="span" title={hint} sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-55') }}>{k}</Typography>
      <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok(tone), textAlign: 'right' }}>{v}</Typography>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography component="h3" sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-50'), mb: 0.5, mt: 0 }}>{title}</Typography>
      {children}
    </Box>
  );
}

const STAGE_TONE = { 1: 'ink-60', 2: 'up', 3: 'text', 4: 'down' };

// The Weinstein weekly stage (DWLF-382): the platform's market-cycle read. Context under forward test, never a signal.
export function StageSection({ stage }) {
  if (stage.stage == null) {
    return (
      <Section title="Weinstein stage · weekly">
        <Row k="Weinstein stage" v="No stage (too little weekly history, or not tracked)" />
      </Section>
    );
  }
  return (
    <Section title="Weinstein stage · weekly">
      <Row
        k="Weinstein stage"
        v={`${stage.stage} · ${cap(stage.stageName)}`}
        tone={STAGE_TONE[stage.stage] || 'text'}
        hint="1 base, 2 advance, 3 top, 4 decline, from the 30-week average"
      />
      <Row
        k="In stage"
        v={Number.isFinite(stage.weeksInStage) ? `${stage.weeksInStage} wk${stage.weeksInStage === 1 ? '' : 's'} · since ${fmtDate(stage.since)}` : '—'}
      />
      {stage.freshness?.stale === true && (
        <Typography data-testid="stage-stale" sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('down'), mt: 0.75 }}>
          {`Stale: no price data since ${fmtDate(stage.freshness.lastBar)}, so this stage is not current.`}
        </Typography>
      )}
      {stage.freshness && stage.freshness.stale == null && (
        <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-45'), mt: 0.75 }}>
          Couldn't check how current this stage's data is.
        </Typography>
      )}
      {stage.verification?.state === 'under-forward-test' && (
        <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-45'), mt: 0.75 }}>
          Context under forward test, not a signal.
        </Typography>
      )}
    </Section>
  );
}

function Brief({ intel, cycle, intelFailed }) {
  const s = intel?.structure;
  const w = intel?.structureWeekly;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {cycle && (
        <Section title="Weekly cycle">
          <CycleRuler cycle={cycle} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography component="span" sx={{ ...LABEL, fontSize: '0.625rem', px: 1, py: 0.5, border: `1px solid ${tok(cycle.stage.token)}`, color: tok(cycle.stage.token) }}>{cycle.stage.label}</Typography>
            {cycle.onDial && (
              <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('ink-60') }}>
                week {Math.round(cycle.weeksSincePivot)} of ~{Number.isFinite(cycle.typicalWeeks) ? cycle.typicalWeeks : '?'}
              </Typography>
            )}
          </Box>
          <Row k="Last confirmed low" v={cycle.window?.ruler ? `${fmtDate(cycle.window.ruler.pivotTime)} · ${fmt(cycle.window.ruler.price)}` : '—'} />
          <Row k="Next low window" v={cycle.window?.opens ? `${fmtDate(cycle.window.opens.time)} → ${fmtDate(cycle.window.closes?.time)}` : '—'} />
          <Row k="Daily pivot" v={cycle.setup?.daily?.status ? `${cycle.setup.daily.status}${Number.isFinite(cycle.setup.daily.confidencePct) ? ` · ${cycle.setup.daily.confidencePct}%` : ''}` : 'None in window'} />
          {cycle.verification?.state === 'unverified' && (
            <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', color: tok('ink-45'), mt: 0.75 }}>Windows are unverified (no claim the low lands inside one).</Typography>
          )}
        </Section>
      )}

      {intelFailed && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('down') }}>Couldn't load the market read.</Typography>}

      {intel?.regime && (
        <Section title="Regime · daily">
          <Row k="Trend" v={cap(intel.regime.trend)} tone={toneOf(intel.regime.trend)} />
          <Row k="Momentum" v={cap(intel.regime.momentum)} tone={toneOf(intel.regime.momentum)} />
          {intel.fsmState && <Row k="Weekly cycle state" v={`${cap(intel.fsmState.name)} · ${intel.fsmState.duration}`} tone={toneOf(intel.fsmState.name)} />}
        </Section>
      )}

      {(s || w) && (
        <Section title="Structure">
          {[['Daily', s], ['Weekly', w]].filter(([, x]) => x).map(([label, x]) => (
            <React.Fragment key={label}>
              <Row k={`${label} swings`} v={`${cap(x.bias)} · ${x.sequence}`} tone={toneOf(x.bias)} />
              <Row k={`${label} flip level`} v={fmt(x.flipLevel)} hint="The price whose decisive break flips this structure's character" />
              <Row k={`${label} trapdoor`} v={fmt(x.trapdoor)} tone="down" hint="The last swing low: losing it means a downside breakdown" />
            </React.Fragment>
          ))}
        </Section>
      )}

      {intel?.stage && <StageSection stage={intel.stage} />}

      {intel?.agentHints?.staleness && (
        <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', lineHeight: 1.6, color: tok('ink-45') }}>{intel.agentHints.staleness}</Typography>
      )}
    </Box>
  );
}

function Events({ intel }) {
  const events = intel?.activeEvents || [];
  if (!events.length) return <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-55') }}>No active events.</Typography>;
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {events.map((e, i) => (
        <Box component="li" key={`${e.event}-${e.since}-${i}`} sx={{ display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr)', gap: 1.5, py: 1.25, borderBottom: `1px solid ${tok('ink-06')}` }}>
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('ink-45') }}>{fmtDate(e.since)}</Typography>
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem', lineHeight: 1.45, color: tok('text') }}>{e.event}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function DeskPanel({ symbol }) {
  const { loading, intel, intelFailed, cycle, pending = [], pendingFailed, dropDecided } = useDeskSymbol(symbol);
  const [tab, setTab] = useState('brief');
  const price = intel?.price;
  const tabs = [
    { id: 'brief', label: 'Brief' },
    { id: 'events', label: 'Events', badge: intel?.activeEvents?.length || 0 },
    { id: 'decide', label: 'Decide', badge: pending.length, hot: pending.length > 0 },
  ];

  return (
    <Box component="aside" aria-label={`Today for ${symbol}`} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ px: 2.5, pt: 2 }}>
        <Typography sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-50') }}>Today for</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography component="h2" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.375rem', fontWeight: 600, m: 0, color: tok('text') }}>{String(symbol).replace('-', '/')}</Typography>
          {price && (
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-70') }}>
              {fmt(price.current)}{' '}
              <Box component="span" sx={{ color: tok(price.change24h >= 0 ? 'up' : 'down') }}>{price.change24h > 0 ? '+' : ''}{price.change24h}%</Box>
            </Typography>
          )}
        </Box>
      </Box>
      <Box role="tablist" aria-label="Panel sections" sx={{ display: 'flex', gap: 2.5, px: 2.5, mt: 1.5, borderBottom: `1px solid ${tok('ink-08')}` }}>
        {tabs.map((t) => (
          <ButtonBase key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            sx={{ ...LABEL, fontSize: '0.6875rem', height: 40, gap: 0.75, color: tab === t.id ? tok('text') : tok('ink-50'), borderBottom: `2px solid ${tab === t.id ? tok('accent') : 'transparent'}` }}>
            {t.label}
            {t.badge > 0 && (
              <Box component="span" sx={{ fontSize: '0.625rem', px: 0.6, backgroundColor: t.hot ? tok('accent') : tok('ink-20'), color: t.hot ? tok('on-accent') : tok('text') }}>{t.badge}</Box>
            )}
          </ButtonBase>
        ))}
      </Box>
      <Box role="tabpanel" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, py: 2 }}>
        {loading && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-50') }}>Loading…</Typography>}
        {!loading && tab === 'brief' && <Brief intel={intel} cycle={cycle} intelFailed={intelFailed} />}
        {!loading && tab === 'events' && <Events intel={intel} />}
        {!loading && tab === 'decide' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pendingFailed && <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('down') }}>Couldn't load pending decisions.</Typography>}
            {!pendingFailed && pending.length === 0 && (
              <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', lineHeight: 1.6, color: tok('ink-55'), border: `1px dashed ${tok('ink-15')}`, p: 2 }}>
                Nothing waiting on this market. When a setup confirms, the call lands here, on Today and on Telegram.
              </Typography>
            )}
            {pending.map((t) => <DecisionCard key={t.tradeId} trade={t} onDecided={dropDecided} />)}
            <Typography sx={{ fontFamily: MONO, fontSize: '0.6875rem', lineHeight: 1.6, color: tok('ink-45') }}>
              Every decision is scored later against what the system would have done. Decision support, not advice.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
